import React, { useState, useEffect } from 'react';
import { updatePortfolioTwo } from '../composables/fetchAPI';
import toast from 'react-hot-toast';
interface ConfigTargetPortRatioProps {
  isOpen: boolean;
  onClose: () => void;
  portfolios: any[];
  onSave: (updatedRatios: { id: string; expectedRatio: number }[]) => void;
}

const ConfigTargetPortRatio: React.FC<ConfigTargetPortRatioProps> = ({ isOpen, onClose, portfolios, onSave }) => {
  const [updatedRatios, setUpdatedRatios] = useState<{ id: string; expectedRatio?: number }[]>(
    portfolios.map((p) => ({ id: p.portId, expectedRatio: p.expectedRatio }))
  );

  const [totalRatio, setTotalRatio] = useState<number>(0);

  useEffect(() => {
    const validRatios = updatedRatios
      .map((item) => item.expectedRatio)
      .filter((v): v is number => typeof v === 'number' && !isNaN(v));
    const total = validRatios.reduce((sum, v) => sum + v, 0);
    setTotalRatio(total);
  }, [updatedRatios]);

  const handleRatioChange = (id: string, ratioStr: string) => {
    setUpdatedRatios((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              expectedRatio: ratioStr === '' ? undefined : parseFloat(ratioStr),
            }
          : item
      )
    );
  };

const handleSave = async () => {
  let filtered = updatedRatios
    .filter((item) => typeof item.expectedRatio === 'number' && !isNaN(item.expectedRatio))
    .map((item) => ({
      id: item.id,
      expectedRatio: item.expectedRatio as number,
    }));

  let total = filtered.reduce((sum, item) => sum + (item.expectedRatio || 0), 0);

  // ตรวจสอบว่าผลรวมเกิน 100% หรือไม่
  if (total > 100) {
    const excess = total - 100;

    // ลดสัดส่วนจากพอร์ตที่มีสัดส่วนมากที่สุดก่อน
    filtered.sort((a, b) => b.expectedRatio - a.expectedRatio);

    for (let i = 0; i < filtered.length; i++) {
      if (filtered[i].expectedRatio! > excess) {
        filtered[i].expectedRatio! -= excess;
        total = 100; // ปรับผลรวมให้เท่ากับ 100%
        break;
      }
    }
  }

  try {
    const failedUpdates: { id: string; expectedRatio: number }[] = [];

    // ส่งข้อมูลไปยัง API ทีละพอร์ต
    for (const { id, expectedRatio } of filtered) {
      const portfolio = portfolios.find((p) => p.portId === id);
      if (!portfolio) continue;

      try {
        await updatePortfolioTwo(id, {
          name: portfolio.name,
          description: portfolio.description,
          expectedRatio,
        });

        // อัปเดตสัดส่วนที่เหลือใน UI
        setUpdatedRatios((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  expectedRatio,
                }
              : item
          )
        );
      } catch (error) {
        console.error(`Error updating portfolio with ID ${id}:`, error);
        failedUpdates.push({ id, expectedRatio }); // เก็บพอร์ตที่อัปเดตไม่สำเร็จ
      }
    }

    // ลองอัปเดตพอร์ตที่ล้มเหลวอีกครั้ง
    for (const { id, expectedRatio } of failedUpdates) {
      const portfolio = portfolios.find((p) => p.portId === id);
      if (!portfolio) continue;

      try {
        await updatePortfolioTwo(id, {
          name: portfolio.name,
          description: portfolio.description,
          expectedRatio,
        });

        // อัปเดตสัดส่วนที่เหลือใน UI
        setUpdatedRatios((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  expectedRatio,
                }
              : item
          )
        );
      } catch (error) {
        console.error(`Retry failed for portfolio with ID ${id}:`, error);
        toast.error(`Failed to update portfolio with ID ${id}`);
      }
    }

    toast.success('อัปเดตสัดส่วนเป้าหมายสำเร็จ!');
    onSave(filtered); // ส่งข้อมูลที่อัปเดตกลับไปยัง parent component
    onClose(); // ปิด popup
  } catch (error) {
    console.error('Error updating portfolio ratios:', error);
    toast.error('เกิดข้อผิดพลาดในการอัปเดตสัดส่วนเป้าหมาย');
  }
};
  if (!isOpen) return null;

  return (
    <div className="bg-[#18181b] rounded-xl shadow-lg p-4 w-[400px] max-w-md flex flex-col h-[450px]">
      <h2 className="text-lg font-bold mb-4">
        ปรับสัดส่วนเป้าหมาย: {isNaN(totalRatio) ? 0 : totalRatio} %
      </h2>
      <div className="my-4">
        <div className="w-full bg-gray-600 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${totalRatio <= 100 ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${Math.min(totalRatio, 100)}%` }}
          ></div>
        </div>
        {totalRatio > 100 && (
          <p className="text-red-400 text-xs mt-1">
            เกินเป้าหมาย {(totalRatio - 100).toFixed(1)}%
          </p>
        )}
      </div>
      <div className="flex-1 overflow-y-auto space-y-4 scrollbar-hide">
        {portfolios.map((portfolio) => (
          <div key={portfolio.portId} className="flex items-center justify-between border border-gray-700 rounded p-2">
            <span className="text-sm text-gray-300 truncate w-[150px]">{portfolio.name}</span>
            <div className="flex items-center">
              <input
                type="number"
                className="bg-gray-700 text-white rounded px-2 py-1 w-[80px]"
                value={
                  updatedRatios.find((item) => item.id === portfolio.portId)?.expectedRatio !== undefined
                    ? updatedRatios.find((item) => item.id === portfolio.portId)?.expectedRatio
                    : ''
                }
                onChange={(e) => handleRatioChange(portfolio.portId, e.target.value)}
                min={0}
              />
              <span className="text-white ml-2">%</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-between">
        <button
          onClick={onClose}
          className="bg-gray-500 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded"
        >
          ยกเลิก
        </button>
        <button
          onClick={handleSave}
          className="bg-green-500 hover:bg-green-600 text-white font-bold px-4 py-2 rounded"
        >
          บันทึก
        </button>
      </div>
    </div>
  );
};

export default ConfigTargetPortRatio;